import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import "./ProductPage.scss";
import dukeElderBook from "../../assets/products/duke-elder-book.png";
import { FaBook, FaCheckCircle, FaExternalLinkAlt } from "react-icons/fa";

const AMAZON_UK =
  "https://www.amazon.co.uk/Ophthalmology-1000-MCQs-Diya-Baker/dp/3032145708";

interface DukeElderPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const DukeElderPage = ({ product }: DukeElderPageProps) => {
  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="Duke Elder examination resource">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <p className="oq-product-hero__eyebrow">Duke Elder examination</p>
              <h1 className="oq-product-hero__title">Duke Elder Examination Resource</h1>
              <p className="duke-elder-hero__subtitle">Ophthalmology in 1000 MCQs</p>
              <p className="duke-elder-hero__authors">
                By Diya Baker, Niha Hussain, Nada Alfahad, Gabriel Bellamy Plaice, Joseph Abbott, and Thomas Jenyon
              </p>
              <p className="duke-elder-hero__tagline">
                A comprehensive MCQ-based revision resource designed to support preparation for the Duke Elder
                Ophthalmology Examination (UK).
              </p>
              <div className="oq-product-hero__signup">
                <Button
                  text={`Buy on Amazon UK — £${product.price}`}
                  linkTo={AMAZON_UK}
                  external
                  type="navy"
                />
              </div>
            </div>
            <div className="oq-product-hero__visual oq-product-hero__visual--book-cover">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={dukeElderBook}
                  alt="Ophthalmology in 1000 MCQs — Duke Elder examination book cover"
                  width={346}
                  height={522}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="product-sales-content">
          <div className="site-container">
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                This book focuses on high-quality multiple-choice questions that reflect the depth, style, and breadth of
                topics tested in the Duke Elder exam. It provides structured exposure to both core fundamentals and
                advanced ophthalmic concepts, making it an invaluable tool for candidates aiming to strengthen exam
                technique and clinical reasoning.
              </p>
              <p className="solution-text">
                Even for those not sitting the Duke Elder exam, this book serves as a valuable global revision resource
                covering essential ophthalmology topics.
              </p>
            </section>

            {/* Book Details and Who It's For Section - Side by Side */}
            <section className="sales-section duke-elder-details-section">
              <div className="duke-elder-two-column">
                <div className="duke-elder-column">
                  <div className="duke-elder-card">
                    <div className="duke-elder-section-header">
                      <FaBook className="duke-elder-inline-icon" />
                      <h2>Book Details</h2>
                    </div>
                    <div className="simple-list">
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p>
                          <strong>Publisher:</strong> Springer Nature Switzerland AG
                        </p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p>
                          <strong>Publication Date:</strong> April 2026
                        </p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p>
                          <strong>ISBN-13:</strong> 978-3032145703
                        </p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p>
                          <strong>ISBN-10:</strong> 3032145708
                        </p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p>
                          <strong>Format:</strong> Hardcover
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="duke-elder-column">
                  <div className="duke-elder-card">
                    <div className="duke-elder-section-header">
                      <FaBook className="duke-elder-inline-icon" />
                      <h2>Who It&apos;s For</h2>
                    </div>
                    <div className="simple-list oq-icon-list">
                      <div className="list-item">
                        <FaCheckCircle aria-hidden />
                        <p>Candidates preparing for the Duke Elder Ophthalmology Examination</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle aria-hidden />
                        <p>Medical students and junior doctors revising ophthalmology</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle aria-hidden />
                        <p>Ophthalmology trainees seeking structured MCQ-based revision</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle aria-hidden />
                        <p>Clinicians looking to refresh core and advanced ophthalmic knowledge</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* What This Book Covers Section */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge">
                  <FaBook className="star-icon" />
                  <span>Comprehensive Coverage</span>
                </div>
                <h2>What This Book Covers</h2>
              </div>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Comprehensive Coverage</h3>
                    <p>Ophthalmic anatomy, physiology, and pathology across all major topics</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Clinical Decision-Making</h3>
                    <p>Diagnostic modalities, therapeutic interventions, and surgical principles</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Exam-Aligned Questions</h3>
                    <p>1,000 MCQs aligned with Duke Elder standards with detailed explanations</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Where to Buy Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Where to Buy</h3>
                <p className="pricing-intro">This book is available from the following retailers:</p>
                <div className="buy-links">
                  <a
                    href={AMAZON_UK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt className="buy-link__icon" aria-hidden />
                    Buy on Amazon (UK)
                  </a>
                  <a
                    href="https://www.amazon.sg/Ophthalmology-1000-MCQs-Diya-Baker/dp/3032145708"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt className="buy-link__icon" aria-hidden />
                    Buy on Amazon (SG)
                  </a>
                  <a
                    href="https://www.waterstones.com/book/ophthalmology-in-1000-mcqs/diya-baker/niha-hussain/9783032145703"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt className="buy-link__icon" aria-hidden />
                    Buy on Waterstones
                  </a>
                  <a
                    href="https://blackwells.co.uk/bookshop/product/Ophthalmology-in-1000-MCQs-by-Diya-Baker-Niha-Hussain-Nada-Alfahad-Gabriel-Bellamy-Plaice-Joseph-Abbott-Thomas-Jenyon/9783032145703"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt className="buy-link__icon" aria-hidden />
                    Buy on Blackwell&apos;s
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DukeElderPage;
